import * as React from 'react';
import { Button, Modal } from '@teamsnap/teamsnap-ui';
import { groupBy, truncate } from 'lodash';
import { differenceInMinutes } from 'date-fns';

// Local imports
import { EventGQLType } from 'core/gql/queries/calendar-events';
import { getAbbreviatedTimezone } from 'core/utils';
import { TEAMSNAP_URL, TOURNAMENTS_ADMIN_URL } from 'core/constants';
import { eventSources, getFormatedEventDateString } from './calendar-utils';

type Event = EventGQLType & {
  startDate: Date;
  endDate: Date;
  position: 'centralized' | 'left' | 'right';
};

export type CalendarEvents = {
  [date: string]: {
    groups: GroupedEvents[];
    individualEvents: Event[];
  };
};

export type GroupedEvents = {
  startsAt: Date;
  endsAt: Date;
  events: Event[];
};

type EventCardProps = Event & {
  maxHeight: number;
  marginLeft: string;
  onClick: () => any;
  GA4data: { user: string; orgId: string };
};

type GroupModalProps = {
  group: GroupedEvents;
  show: boolean;
  onClose: () => any;
  GA4data: { user: string; orgId: string };
};

type EventModalProps = { event: Event; show: boolean; onClose: () => any; GA4data: { user: string; orgId: string } };

const TIMEZONE = getAbbreviatedTimezone();

const formatHours = (date: Date) => {
  const mins = `${date.getMinutes()}`;
  const formattedMins = mins.length > 1 ? mins : `0${mins}`;
  const period = date.getHours() > 11 ? 'pm' : 'am';
  const hours = (period === 'am' ? date.getHours() : date.getHours() - 12) || 12;

  return mins === '0' ? `${hours}${period}` : `${hours}:${formattedMins}${period}`;
};

const formatName = ({ type, name }: Event) => {
  if (type === 'Game') return 'Game';

  return truncate(name, { length: 13 }) || 'Event';
};

const joinTeams = (event: Event) =>
  event.teams.length < 3
    ? event.teams.map((x) => x.teamName || '(Team not assigned)').join(' vs. ')
    : `${event.teams[0].teamName} and ${event.teams.length - 1} more teams`;

export const formatLocation = ({ name, venueName }: { name: string; venueName: string }) =>
  venueName ? `${name} - ${venueName}` : name;

const getSourceLink = (event: Event, GA4data: { user: string; orgId: string }) => {
  const {
    type,
    calendarSource: source,
    linkInfo: { eventId, teamId, divisionId, scheduleItemId },
  } = event;

  const link = [
    ['team', `${TEAMSNAP_URL}/${teamId}/schedule/view_game/${eventId}`],
    ['cl_season', `${TEAMSNAP_URL}/${divisionId}/league_schedule/view_event/${eventId}`],
    [
      'tst',
      `${TOURNAMENTS_ADMIN_URL}/event/${eventId}/${
        type === 'Game' ? 'timeline' : 'practices'
      }?divisionId=${divisionId}&scheduleItemId=${scheduleItemId}`,
    ],
    [
      'cl_scheduler',
      `${TEAMSNAP_URL}/${divisionId}/league_schedule/view_${type === 'Game' ? 'game' : 'event'}/${eventId}`,
    ],
  ].find(([key]) => key === source)[1];

  return (
    <p>
      via{' '}
      <a
        rel="noreferrer"
        target="_blank"
        href={link}
      >
        {eventSources[event.calendarSource]}
      </a>
    </p>
  );
};

export const GroupModal = ({ show, onClose, group, GA4data }: GroupModalProps) => {
  const groupedEvents = groupBy(group?.events || [], ({ startDate }) => startDate.getHours());
  let counter = 0;

  return (
    <Modal
      showClose
      allowOverlayClose
      show={show}
      closeFn={onClose}
      heading={`${group?.events?.length} events on ${!group ? '' : `${getFormatedEventDateString(group.startsAt)}`}`}
      style={{ minWidth: '0', width: '475px' }}
    >
      <div id="grouped-events-modal">
        {group &&
          Object.values(groupedEvents).map((events) => (
            <div className="u-flex">
              <div className="u-size2of12">
                {formatHours(events[0].startDate)} ({TIMEZONE})
              </div>
              <div className="u-size10of12">
                {events
                  .sort((a, b) => (a.endDate < b.endDate ? -1 : 1))
                  .map((event) => {
                    counter++;

                    return (
                      <div className={`u-padSm${counter % 2 === 0 ? ' bg-grey' : ''}`}>
                        <p className="u-fontSizeMd u-textSemiBold">{event.type === 'Game' ? 'Game' : event.name}</p>
                        <p>{joinTeams(event)}</p>
                        <p>{event.seasonFullName}</p>
                        <p>
                          {formatHours(event.startDate)} - {formatHours(event.endDate)} ({TIMEZONE})
                        </p>
                        <p>{formatLocation(event.location)}</p>
                        {getSourceLink(event, GA4data)}
                      </div>
                    );
                  })}
              </div>
            </div>
          ))}
        <div className="u-flex u-flexJustifyEnd">
          <Button onClick={onClose} color="primary" className="Button u-spaceTopLg">
            Close
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export const EventModal = ({ show, onClose, event, GA4data }: EventModalProps) => (
  <Modal
    showClose
    allowOverlayClose
    show={show}
    closeFn={onClose}
    heading={`${event?.type === 'Game' ? 'Game' : 'Event'} details for ${getFormatedEventDateString(event?.startDate)}`}
    style={{ minWidth: '0', width: '475px' }}
  >
    {event && (
      <>
        <p className="u-fontSizeMd u-textSemiBold">
          {event.type === 'Game' ? `Game: ${joinTeams(event)}` : event.name}
        </p>
        <p className="u-spaceBottomMd">
          {formatHours(event.startDate)} - {formatHours(event.endDate)} ({TIMEZONE})
        </p>
        <p>{joinTeams(event)}</p>
        {event.division && <p>{event.division?.name}</p>}
        <p>{event.seasonFullName}</p>
        <p className="u-spaceEndsMd">{formatLocation(event.location)}</p>
        {getSourceLink(event, GA4data)}
      </>
    )}
    <div className="u-flex u-flexJustifyEnd">
      <Button onClick={onClose} color="primary" className="Button u-spaceTopLg">
        Close
      </Button>
    </div>
  </Modal>
);

export const GroupCard = ({ onClick, startsAt, endsAt, events }: GroupedEvents & { onClick: () => any }) => {
  const maxDate =
    startsAt.getDate() === endsAt.getDate() ? endsAt : new Date(new Date(startsAt).setHours(23, 59, 0, 0));

  const height = Math.round(differenceInMinutes(maxDate, startsAt) / 15) * 25;

  return (
    <div
      onClick={onClick}
      role="none"
      className="calendar-event pointer"
      style={{ width: '10.8vw', height: `${height || 25}px` }}
    >
      <p className="event-title">{events.length} events</p>
      <p>
        Between {formatHours(startsAt)}-{formatHours(endsAt)} ({TIMEZONE})
      </p>
    </div>
  );
};

export const EventCard = ({ onClick, marginLeft, maxHeight, GA4data, ...event }: EventCardProps) => {
  const height = Math.round(differenceInMinutes(event.endDate, event.startDate) / 15) * 25;
  const width = `${event.position === 'centralized' ? 100 : 50}%`;

  return (
    <div
      onClick={onClick}
      role="none"
      className="calendar-event pointer"
      style={{ maxHeight, width, marginLeft, height: `${height || 25}px` }}
    >
      <p className="event-title">{formatName(event)}</p>
      <p className="u-spaceBottomXs">
        {formatHours(event.startDate)} - {formatHours(event.endDate)} ({TIMEZONE})
      </p>
      <p>{joinTeams(event)}</p>
      {event.division && <p>{event.division?.name}</p>}
      <p>{event.seasonFullName}</p>
      <p className="u-spaceEndsXs">{formatLocation(event.location)}</p>
      {getSourceLink(event, GA4data)}
    </div>
  );
};

export default Event;
